import React, { useMemo, useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import {
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
import Table from '../common/Table';
import Modal from '../common/Modal';
import FormInput from '../common/FormInput';
import { showSuccess, showError } from '../../utils/notificationHelper';
import { useFetch, useSubmission } from '../../hooks/useCallApi';

const PermitRequirementsModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [requirementToDelete, setRequirementToDelete] = useState(null);
  const [togglingRequirement, setTogglingRequirement] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 5,
  });

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    required: false
  });

  const params = useMemo(() => ({
    per_page: pagination.perPage,
    page: pagination.currentPage,
    search: searchValue || undefined,
  }), [pagination.perPage, pagination.currentPage, searchValue]);

  const {
    data: requirementsResponse,
    isLoading: requirementsLoading,
  } = useFetch(
    ['permit-requirements', params],
    (api) => api.admin.businessPermitsAPI.getAllRequirements(params),
    {
      keepPreviousData: true,
      staleTime: 30 * 1000,
      enabled: isOpen,
    }
  );

  const requirementsData = requirementsResponse?.data ?? requirementsResponse ?? {};
  const requirementsList =
    requirementsData?.permit_requirements ||
    requirementsData?.requirements ||
    (Array.isArray(requirementsData) ? requirementsData : null) ||
    (Array.isArray(requirementsResponse) ? requirementsResponse : []) ||
    [];

  const paginationData =
    requirementsData?.meta ||
    requirementsResponse?.meta ||
    requirementsData?.pagination ||
    requirementsResponse?.pagination;

  const totalItems =
    paginationData?.total ??
    requirementsData?.total ??
    requirementsResponse?.total ??
    requirementsList.length;

  const lastPage =
    paginationData?.last_page ??
    requirementsData?.last_page ??
    requirementsResponse?.last_page ??
    Math.max(1, Math.ceil((totalItems || 0) / (pagination.perPage || 1)));

  const currentPageFromServer = paginationData?.current_page ?? pagination.currentPage;

  const { mutateAsync: createRequirement, isLoading: isCreating } = useSubmission(
    ['create-permit-requirement'],
    (api, data) => api.admin.businessPermitsAPI.createRequirement(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['permit-requirements']);
      },
    }
  );

  const { mutateAsync: updateRequirement, isLoading: isUpdating } = useSubmission(
    ['update-permit-requirement'],
    (api, { id, data }) => api.admin.businessPermitsAPI.updateRequirement(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['permit-requirements']);
      },
    }
  );

  const { mutateAsync: archiveRequirement, isLoading: isArchiving } = useSubmission(
    ['archive-permit-requirement'],
    (api, id) => api.admin.businessPermitsAPI.archiveRequirement(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['permit-requirements']);
      },
    }
  );

  const { mutateAsync: updateRequirementStatus } = useSubmission(
    ['update-permit-requirement-status'],
    (api, { id, data }) => api.admin.businessPermitsAPI.updateStatusRequirement(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['permit-requirements']);
      },
    }
  );

  const columns = [
    {
      key: 'name',
      label: 'Requirement Name',
      render: (value) => <div className="text-sm text-gray-900">{value}</div>,
    },
    {
      key: 'is_required',
      label: 'Required?',
      render: (_, row) => {
        const isRequired = row.required ?? row.is_required ?? false;
        return (
          <div className="flex justify-center">
            {isRequired ? (
              <CheckCircleIconSolid className="w-5 h-5 text-blue-600" />
            ) : (
              <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => <StatusBadge status={value} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleEdit(row)}
            className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
            title="Edit"
            type="button"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => toggleRequirementStatus(row)}
              disabled={togglingRequirement === row.id}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${row.status === 'active' ? 'bg-blue-600' : 'bg-gray-300'
                } ${togglingRequirement === row.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              title="Toggle Status"
              type="button"
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200 ${row.status === 'active' ? 'translate-x-4' : 'translate-x-0.5'
                  }`}
              />
            </button>
          </div>
          <button
            onClick={() => handleDelete(row)}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Delete"
            type="button"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handlePerPageChange = (newPerPage) => {
    setPagination((prev) => ({ ...prev, perPage: newPerPage, currentPage: 1 }));
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (showEditModal && selectedRequirement) {
        await updateRequirement({
          id: selectedRequirement.id,
          data: {
            name: formData.name,
            is_required: formData.required,
            status: selectedRequirement.status,
          },
        });
        showSuccess('Requirement updated successfully');
        setShowEditModal(false);
        setSelectedRequirement(null);
      } else {
        await createRequirement({
          name: formData.name,
          is_required: formData.required,
          status: 'active',
        });
        showSuccess('Requirement created successfully');
        setShowNewModal(false);
      }

      setFormData({ name: '', required: false });
    } catch (err) {
      console.error('Failed to submit requirement:', err);
      showError('Failed to save requirement. Please try again.');
    }
  };

  const toggleRequirementStatus = async (requirement) => {
    const id = requirement?.id;
    if (!id) return;

    const currentStatus = requirement?.status;
    const nextStatus = currentStatus === 'active' ? 'inactive' : 'active';

    try {
      setTogglingRequirement(id);
      await updateRequirementStatus({
        id,
        data: { status: nextStatus },
      });
      showSuccess(`Requirement marked as ${nextStatus}`);
    } catch (err) {
      console.error('Failed to update requirement status:', err);
      showError('Failed to update requirement status.');
    } finally {
      setTogglingRequirement(null);
    }
  };

  // Handle edit
  const handleEdit = (requirement) => {
    setSelectedRequirement(requirement);
    setFormData({
      name: requirement.name ?? '',
      required: !!(requirement.required ?? requirement.is_required),
    });
    setShowEditModal(true);
  };

  // Handle delete
  const handleDelete = (requirement) => {
    setRequirementToDelete(requirement);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!requirementToDelete?.id) return;

    try {
      await archiveRequirement(requirementToDelete.id);
      showSuccess('Requirement archived successfully');
      setShowDeleteModal(false);
      setRequirementToDelete(null);
    } catch (err) {
      console.error('Failed to archive requirement:', err);
      showError('Failed to delete requirement. Please try again.');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" title="Business Permit Requirements">
      <div className="space-y-4">
        {/* Header with Icon, Title, Search and New Button */}
        <div className="bg-white rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">List of Business Permit Requirements</h2>
              </div>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Search requirement by name, status..."
                    className="w-64 pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <button
                  onClick={() => {
                    setFormData({ name: '', required: false });
                    setShowNewModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  <PlusIcon className="w-5 h-5" />
                  New Requirement
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          data={requirementsList || []}
          getRowKey={(row) => row.id}
          pagination={{
            currentPage: currentPageFromServer,
            perPage: pagination.perPage,
            lastPage,
            total: totalItems,
          }}
          onPageChange={handlePageChange}
          paginationProps={{
            onPerPageChange: handlePerPageChange,
            isLoading: requirementsLoading,
            showSizeSelector: true,
          }}
          isLoading={requirementsLoading}
        />
      </div>

      {/* New Requirement Modal */}
      <Modal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        title="New Requirement"
        size="md"
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <FormInput
              label="Requirement Name"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter requirement name"
            />

            <div className="flex items-center">
              <input
                type="checkbox"
                id="required"
                checked={formData.required}
                onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="required" className="ml-2 text-sm text-gray-700">
                Required
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowNewModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isCreating}
            >
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Requirement Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Requirement"
        size="md"
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <FormInput
              label="Requirement Name"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter requirement name"
            />

            <div className="flex items-center">
              <input
                type="checkbox"
                id="required-edit"
                checked={formData.required}
                onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="required-edit" className="ml-2 text-sm text-gray-700">
                Required
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isUpdating}
            >
              {isUpdating ? 'Updating...' : 'Update Changes'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Requirement"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete "{requirementToDelete?.name}"? This action cannot be undone.
          </p>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={isArchiving}
            onClick={handleConfirmDelete}
          >
            {isArchiving ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </Modal>
  );
};

export default PermitRequirementsModal;
